import { useEffect, useRef } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMe } from "@/features/auth/useMe";
import { useAuthStore } from "@/stores/auth";

/**
 * `/hub` — role-based redirect (M12a).
 *
 * Priority order (most-coach-shaped first):
 *   - coach / assistant_coach / team_admin / global_admin
 *                                        → `/coach/review`
 *   - uploader (with no coach/admin role) → `/admin`
 *   - guardian / player / viewer-only    → `/feedback`
 *   - anonymous                          → `/login?next=/hub`
 *
 * The Hub button on the marketing landing reads "tktic hub" — the
 * primary motion of the product is coach → review footage → publish
 * feedback. Coaches, assistant coaches, team admins, and global
 * admins all land on Coach Review (the focused canvas + composer
 * surface) because that's where they do their job. Pure uploaders
 * (a rare admin-only role with no membership) go to `/admin`
 * because they have no Coach access. Viewers / guardians / players
 * go to `/feedback` because that's their entire surface.
 *
 * Notably: a global admin who is *also* a team_admin (the common
 * case for `admin-global` once they accept a team invite) lands on
 * Coach Review, not `/admin`. They can reach `/admin` via the
 * top-nav at any time.
 *
 * `useRef` guards the redirect so TanStack Router's parent-stays-mounted
 * behavior across an intra-effect navigate doesn't fire the redirect a
 * second time (same load-bearing pattern as M9a / M9b auth gates).
 */
export function HubRedirect() {
  const navigate = useNavigate();
  const meQuery = useMe();
  const authMe = useAuthStore((s) => s.me);
  const hydrating = useAuthStore((s) => s.hydrating);
  const redirected = useRef(false);

  useEffect(() => {
    if (redirected.current) return;
    if (hydrating || meQuery.isLoading) return;

    const user = authMe?.user;
    if (!user) {
      redirected.current = true;
      void navigate({
        to: "/login",
        search: { next: "/hub" },
        replace: true,
      });
      return;
    }

    const roles = user.roles ?? [];
    const memberships = authMe?.memberships ?? [];
    const isGlobalAdmin = !!user.is_global_admin || roles.includes("admin");
    const isUploader = roles.includes("uploader");
    const isTeamAdmin = memberships.some((m) => m.role === "team_admin");
    const isCoach = memberships.some(
      (m) => m.role === "coach" || m.role === "assistant_coach",
    );

    redirected.current = true;
    if (isCoach || isTeamAdmin || isGlobalAdmin) {
      // Coach Review is the primary motion of the product; land
      // every coach-shaped user (including team_admin / global_admin
      // who almost always coach too) there. The Hub button on the
      // marketing landing reads "tktic hub", and the hub work is
      // reviewing footage + publishing feedback.
      void navigate({ to: "/coach/review", replace: true });
    } else if (isUploader) {
      // Pure uploader with no coach/admin role — narrow surface,
      // their job is /admin/matches.
      void navigate({ to: "/admin", replace: true });
    } else {
      void navigate({ to: "/feedback", replace: true });
    }
  }, [authMe, hydrating, meQuery.isLoading, navigate]);

  return (
    <main
      data-testid="hub-redirect"
      className="flex min-h-[40vh] items-center justify-center bg-canvas text-ink"
    >
      <p className="font-mono text-sm text-ink-muted">Opening your hub…</p>
    </main>
  );
}
